import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';

const InvoicesList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data for invoices
  const invoices = [
    { id: '1', invoice_number: 'INV-00245', customer: 'John Doe', total_amount: 1250000, status: 'paid', due_date: '2023-06-20', created_at: '2023-06-15' },
    { id: '2', invoice_number: 'INV-00244', customer: 'Jane Smith', total_amount: 850000, status: 'unpaid', due_date: '2023-06-19', created_at: '2023-06-14' },
    { id: '3', invoice_number: 'INV-00243', customer: 'Robert Johnson', total_amount: 2100000, status: 'unpaid', due_date: '2023-06-18', created_at: '2023-06-13' },
    { id: '4', invoice_number: 'INV-00242', customer: 'Emily Davis', total_amount: 950000, status: 'cancelled', due_date: '2023-06-17', created_at: '2023-06-12' },
  ];

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      sent: 'Sent',
      paid: 'Paid',
      unpaid: 'Unpaid',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'draft':
        return 'badge-warning';
      case 'sent':
        return 'badge-info';
      case 'paid':
        return 'badge-success';
      case 'unpaid':
        return 'badge-warning';
      case 'cancelled':
        return 'badge-error';
      default:
        return 'badge';
    }
  };

  const getFormattedAmount = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        <Button variant="primary" onClick={() => navigate('/invoices/new')}>
          Create New Invoice
        </Button>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <select
            className="input-field"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Invoice Number', 'Customer', 'Amount', 'Status', 'Due Date', 'Date', 'Actions']}
          rows={filteredInvoices.map(invoice => [
            invoice.invoice_number,
            invoice.customer,
            getFormattedAmount(invoice.total_amount),
            <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
              {getStatusLabel(invoice.status)}
            </span>,
            invoice.due_date,
            invoice.created_at,
            <div className="flex space-x-2">
              <button
                onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                className="text-primary-600 hover:text-primary-900"
              >
                View
              </button>
            </div>
          ])}
          renderRow={(row, index) => {
            const invoice = filteredInvoices[index];
            return (
              <tr key={invoice.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[5]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[6]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const invoice = filteredInvoices[index];
            return (
              <Card key={invoice.id} className="mb-4">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {row[0]}
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <p className="text-sm font-medium text-gray-900">{row[3]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Due Date</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="text-sm font-medium text-gray-900">{row[5]}</p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No invoices found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesList;